import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, needsOnboarding, needsOrganizationSelection, user, activeOrganization } = useAuth();
  const location = useLocation();
  const query = useQuery();
  const isCreatingNewOrg = location.pathname.startsWith('/onboarding') && query.get('mode') === 'create';

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Debug logging
  console.log('ProtectedRoute:', { 
    pathname: location.pathname, 
    isAuthenticated, 
    needsOnboarding, 
    needsOrganizationSelection, 
    orgsCount: user?.organizations?.length || 0,
    hasActiveOrg: !!activeOrganization
  });

  // Redirect to organization selection if user has organizations but no active one (and isn't already there)
  // Allow onboarding when creating a new organization
  if (needsOrganizationSelection && 
      !location.pathname.startsWith('/select-organization') && 
      !isCreatingNewOrg) {
    console.log('Redirecting to /select-organization');
    return <Navigate to="/select-organization" replace />;
  }

  // Redirect to onboarding if user has no organization (and isn't already there)
  if (needsOnboarding && !location.pathname.startsWith('/onboarding')) {
    console.log('Redirecting to /onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  // Render protected content
  return <>{children}</>;
};

export default ProtectedRoute;
